// @ts-strict-ignore
import { ConfiguratorParameters } from '@3d-scene/configurator-parameters/configurator-parameters';
import { CompositeArticleUtils } from '@article/util/composite-article-utils';
import { PropertyUtils } from '@article/util/property-utils';
import { ITableProvider } from '@project/components/label/label-editor/table-provider';
import { IConnectionSpanData, isConnectionSpanData, ISpanData } from '@project/components/label/label-editor/span-data';
import { MetaElementBehaviorPropertyDef } from '@project/constants/meta-element-behavior-property-def';
import { LocalizedPropertyService } from '@shared/localization/localized-property.service';
import { CompressionSimpleTextValueVO, RGBColorValueVO, SimpleTextValueVO, TableTextItemVO, TableTextRowVO, TableTextValueVO, TextureAreaUsageVO, TextureAreaVO, TextValueVO } from '@shared/vo/meta-element.vo';
import { StringUtils } from '@shared/util/string-utils';
import { ProjectModel } from '@project/model/project.model';
import { TableProviderUtils } from '@project/components/label/label-editor/service/table-provider-utils';
import { TextureUtils } from '@3d-scene/model3d/util/texture-utils';
import { Injectable } from '@angular/core';

class TableIterationInfo {
  public startIndex: number;
  public count: number;
  public step: number;

  constructor(startIndex: number, count: number, step: number) {
    this.startIndex = startIndex;
    this.count = count;
    this.step = step;
  }
}

@Injectable({ providedIn: 'root' })
export class TableProviderPersister {
  constructor(
    private projectModel: ProjectModel,
    private localizedPropertyService: LocalizedPropertyService,
    private compositeArticleUtils: CompositeArticleUtils,
  ) {}

  public apply(tableProvider: ITableProvider): void {
    let rowIndex: number = 0;
    for (const lineSpan of tableProvider.lineSpans) {
      this.applyTextToConnectionsInCorridor(tableProvider, rowIndex, lineSpan);
      rowIndex += lineSpan;
    }
  }

  private applyTextToConnectionsInCorridor(tableProvider: ITableProvider, rowIndex: number, lineSpan: number): void {
    let colIndex: number = 0;
    const row: ISpanData[] = tableProvider.getRow(rowIndex);
    const colCount: number = row ? row.length : 0;

    while (colIndex < colCount) {
      const mergedColCount: number = TableProviderUtils.getMergedColCount(tableProvider, rowIndex, lineSpan, colIndex);
      this.applyTextToMergedConnections(tableProvider, rowIndex, lineSpan, colIndex, mergedColCount);
      colIndex += mergedColCount;
    }
  }

  private applyTextToMergedConnections(
    tableProvider: ITableProvider,
    rowIndex: number,
    lineSpan: number,
    colIndex: number,
    colCount: number,
  ): void {
    const startSpanData: ISpanData = tableProvider.getSpanData(rowIndex, colIndex);
    if (!isConnectionSpanData(startSpanData)) {
      // ignore span-data created for end-plate
      return;
    }

    const connectionSpanData: IConnectionSpanData = startSpanData;

    let textValue: TextValueVO = null;
    const isTableData: boolean = connectionSpanData.rowSpan < lineSpan;
    if (isTableData) {
      textValue = this.createTableTextValue(
        tableProvider,
        new TableIterationInfo(rowIndex, lineSpan / connectionSpanData.rowSpan, connectionSpanData.rowSpan),
        new TableIterationInfo(colIndex, colCount, 1),
      );
    } else {
      textValue = this.createSimpleTextValue(connectionSpanData);
    }
    (connectionSpanData.metaElementUsage as TextureAreaUsageVO).textValue = textValue;

    // update meta-info set to texture-area-usages
    this.updateMergedTextureUsages(tableProvider, rowIndex, colIndex, colCount);
  }

  private createTableTextValue(
    tableProvider: ITableProvider,
    rowIterator: TableIterationInfo,
    colIterator: TableIterationInfo,
  ): TableTextValueVO {
    const tableValue: TableTextValueVO = new TableTextValueVO();

    for (let rowIndex: number = 0; rowIndex < rowIterator.count; rowIndex++) {
      const spanDataRowIndex: number = rowIndex * rowIterator.step + rowIterator.startIndex;
      const tableRow: TableTextRowVO = new TableTextRowVO();
      for (let colIndex: number = 0; colIndex < colIterator.count; colIndex++) {
        const spanDataColIndex: number = colIndex * colIterator.step + colIterator.startIndex;
        const spanData: ISpanData = tableProvider.getSpanData(spanDataRowIndex, spanDataColIndex);

        if (!spanData.parent && isConnectionSpanData(spanData)) {
          const rowItem: TableTextItemVO = this.createTableTextItem(spanData);
          // re-calculate colSpan since end-plate span datas should be ignored
          // so colSpan set to TableTextItemVO could be less than actual colSpan of the given span-data
          rowItem.colSpan = this.getConnectionColCount(tableProvider, spanDataRowIndex, spanDataColIndex, spanData.colSpan);
          tableRow.items.push(rowItem);
        }
      }
      tableValue.rows.push(tableRow);
    }

    return tableValue;
  }

  private getConnectionColCount(tableProvider: ITableProvider, startRowIndex: number, startColIndex: number, mergedColCount: number): number {
    let colCount: number = 0;
    for (let colIndex: number = startColIndex; colIndex < startColIndex + mergedColCount; colIndex++) {
      const spanData: ISpanData = tableProvider.getSpanData(startRowIndex, colIndex);
      if (spanData && !spanData.isEndPlate) {
        colCount++;
      }
    }
    return colCount;
  }

  private createTableTextItem(spanData: IConnectionSpanData): TableTextItemVO {
    const item: TableTextItemVO = new TableTextItemVO();
    item.value = this.createSimpleTextValue(spanData);
    item.rowSpan = spanData.rowSpan;
    item.colSpan = spanData.colSpan;
    return item;
  }

  private createSimpleTextValue(spanData: IConnectionSpanData): SimpleTextValueVO {
    const simpleValue: CompressionSimpleTextValueVO = new CompressionSimpleTextValueVO();
    if (spanData.metaElement instanceof TextureAreaVO) {
      if (this.projectModel.user && this.projectModel.user.hasPermission("USE_ADVANCED_TEXT_EDIT_FUNCTIONS")) {
        simpleValue.compression = spanData.compression;
        simpleValue.autoCompression = spanData.autoFormatByTextCheck;
        simpleValue.autoFontSize = spanData.autoFormatByTextCheck;
      } else {
        simpleValue.compression = spanData.compression;
      }
    }
    simpleValue.bgColor = new RGBColorValueVO();
    simpleValue.hAlignment = 0;
    simpleValue.vAlignment = TextureUtils.getIndexByVerticalAlign(spanData.labelFormat.verticalAlign);
    simpleValue.rotation = spanData.labelFormat.direction;

    const match = spanData.data?.match(/COLOR="([^"]+)"/i);
    const userDefinedColor = match ? match[1] : "#000000";
    const textColor = this.getTextForegroundColor(spanData);

    if (userDefinedColor !== "#000000") {
      simpleValue.text = spanData.data;
    } else {
      simpleValue.text = StringUtils.switchColorOfText(spanData.data, textColor);
    }
    return simpleValue;
  }

  private getTextForegroundColor(connectionSpanData: IConnectionSpanData): string {
    const connectionFontColor = PropertyUtils.getStringPropertyValue(connectionSpanData.metaElement.behaviorProperties, MetaElementBehaviorPropertyDef.DEFAULT_FONT_COLOR_HEX);

    if (connectionFontColor) return connectionFontColor;

    // if the fontcolor is not defined for the connection, get a matching color for the articles color (black article -> white font...)
    let colorName = this.localizedPropertyService.getEnglishColorNameFromArticle(connectionSpanData.article);
    if (!colorName) {
      // in many cases the color is only defined on the compositearticle, so this is our backup.
      const compositeArticle = this.compositeArticleUtils.getCompositeArticle(connectionSpanData.connection.articleContainer);
      colorName = this.localizedPropertyService.getEnglishColorNameFromArticle(compositeArticle);
    }
    return ConfiguratorParameters.directPrintingColorToBackground.get(colorName) ?? "#000000";
  }

  private updateMergedTextureUsages(tableProvider: ITableProvider, startRowIndex, startColIndex, colCount): void {
    let previousTextureUsage: TextureAreaUsageVO = null;
    for (let colIndex: number = 0; colIndex < colCount; colIndex++) {
      const spanData: ISpanData = tableProvider.getSpanData(startRowIndex, startColIndex + colIndex);
      if (!spanData || !isConnectionSpanData(spanData)) {
        continue;
      }

      const connectionSpanData: IConnectionSpanData = spanData;
      const textureUsage: TextureAreaUsageVO = connectionSpanData.metaElementUsage as TextureAreaUsageVO;
      if (previousTextureUsage) {
        textureUsage.previousMergedUsageId = previousTextureUsage.identity;
        // reset value in merged connection
        textureUsage.textValue = new SimpleTextValueVO();

        previousTextureUsage.nextMergedUsageId = textureUsage.identity;
      } else {
        textureUsage.previousMergedUsageId = null;
      }

      previousTextureUsage = textureUsage;
    }
    // texture-usage of the last connection
    if (previousTextureUsage) {
      previousTextureUsage.nextMergedUsageId = null;
    }
  }
}
